<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import { toast } from "../js/toast.js";
    import type { ApiResponse } from '../js/apiTypes.js';

    import Modal from './Modal.svelte';
    import ModalConfirm from './ModalConfirm.svelte';
    import ImageGallery from './ImageGallery.svelte';
    import Image from './Image.svelte';
    import { getNestedProperty } from '../js/utils.js';
    import {
        emptyTableData,
        getSelectOptions,
        optionProp,
        type FormDataRecord,
        type GalleryMediaFile,
        type MediaFile,
        type TableData,
        type TableField,
        toGalleryMediaFiles,
    } from '../js/tableTypes.js';

    export let modelId: string | null = '';
    export let modelName = '';
    export let apiResponse = false;
    export let displayModal = false;
    export let tableData: TableData = emptyTableData;
    export let customSubmit: ((data: FormData) => void) | null = null;

    const dispatch = createEventDispatcher<{
        saved: { detail: unknown };
        mediaDeleted: void;
        fileSelected: { url: string };
    }>();

    let currentId: string | null = '';
    let modalWidth = 450;
    let isEditing = false;
    let displayConfirm = false;
    let dirty = false;
    let selectedFiles: FileList | null = null;
    let fileInput: HTMLInputElement;
    let singleMediaUrl = '';
    let errors: Record<string, string> = {};

    function triggerFileInput() {
        fileInput.click();
    }

    async function handleImageDelete(event: CustomEvent<{ uuid: string }>) {
        const deletedUuid = event.detail.uuid;
        await api.crudDelete('media-file', deletedUuid);
        toast.showNotification(!!$api.success, $api.message ?? '');
        const mediaFiles = formData.media_files ?? [];
        formData.media_files = mediaFiles.filter((image: MediaFile) => image.uuid !== deletedUuid);
        syncGalleryMediaFiles();
        if (Object.prototype.hasOwnProperty.call(formData, 'media_file')) {
            delete formData.media_file;
            singleMediaUrl = '';
        }
        dispatch('mediaDeleted');
    }

    function handleSingleMediaDelete() {
        const mediaFile = formData.media_file;
        if (mediaFile?.uuid) {
            void handleImageDelete(new CustomEvent('delete', { detail: { uuid: mediaFile.uuid } }));
        }
    }

    const handleFileSelection = () => {
        if (!selectedFiles || selectedFiles.length === 0) return;
        setDirty();
        if (tableData.details.hasMediaFiles) {
            const existing = formData.media_files ?? [];
            formData.media_files = [
                ...existing,
                ...Array.from(selectedFiles).map((file) => ({
                    url: URL.createObjectURL(file),
                    file_name: file.name,
                })),
            ];
            syncGalleryMediaFiles();
        } else {
            const file = selectedFiles[0];
            const url = URL.createObjectURL(file);
            if (!formData.media_file) {
                formData.media_file = {};
            }
            formData.media_file.url = url;
            singleMediaUrl = url;
            dispatch('fileSelected', { url });
        }
    };

    const confirmHide = () => {
        if (dirty) {
            displayConfirm = true;
            return;
        }
        hide();
    };

    const hide = () => {
        displayModal = false;
        isEditing = false;
    };

    const setDirty = () => {
        dirty = true;
    };

    let formData: FormDataRecord = {};

    async function save() {
        if (validateForm()) {
            const data = new FormData();

            if (Object.prototype.hasOwnProperty.call(formData, 'media_file')) {
                delete formData.media_file;
            }

            if (Object.prototype.hasOwnProperty.call(formData, 'media_files')) {
                delete formData.media_files;
            }

            if (selectedFiles) {
                if (tableData.details.hasMediaFiles) {
                    Array.from(selectedFiles).forEach((file, index) => {
                        data.append(`media_files[${index}]`, file);
                    });
                } else {
                    data.append('media_file', selectedFiles[0]);
                }
            }

            tableData.fields.forEach((field: TableField) => {
                if (field.editable || modelId === null) {
                    const key = field.model_key !== undefined ? field.model_key : field.key;
                    const value = formData[field.key];
                    data.append(key, value != null ? String(value) : '');
                }
            });

            if (customSubmit) {
                customSubmit(data);
            } else {
                let response: ApiResponse;
                if (modelId === null) {
                    response = await api.crudCreate(modelName, data) as ApiResponse;
                } else {
                    response = await api.crudUpdate(modelName, modelId, data) as ApiResponse;
                }

                if (response.success) {
                    hide();
                    dispatch('saved', { detail: response.data });
                    dirty = false;
                    apiResponse = true;
                }
                toast.showNotification(!!$api.success, $api.message ?? '');
            }
        }
    }

    function validateForm() {
        let valid = true;
        errors = {};
        tableData.fields.forEach((element: TableField) => {
            if (element.isMandatory && !formData[element.key]) {
                errors[element.key] = `${element.label} is required`;
                valid = false;
            }
        });
        return valid;
    }

    const edit = () => {
        isEditing = true;
    };

    $: if (displayModal === true) {
        refreshData();
    }

    $: {
        if (modelId !== currentId || modelId === null) {
            refreshData();
        }
        if (currentId === null) {
            edit();
        }
    }

    async function refreshData() {
        formData = {};
        errors = {};

        if (modelId !== null) {
            await fetchData();
        }

        updateFormData();

        currentId = modelId;
        dirty = false;
    }

    async function fetchData() {
        if (modelId === null) return;
        const response = await api.crudGet(modelName, modelId) as ApiResponse<FormDataRecord>;
        formData = { ...(response.data ?? {}) };
        singleMediaUrl = formData.media_file?.url ?? '';
        syncGalleryMediaFiles();
    }

    function updateFormData() {
        for (const field of tableData.fields) {
            if (field.type === 'select' && field.value) {
                formData[field.key] = getNestedProperty(formData, field.value);
            }
        }
        selectedFiles = null;
        syncGalleryMediaFiles();
    }

    function isFieldDisabled(field: TableField): boolean {
        return modelId !== null && (!isEditing || !field.editable);
    }

    let galleryMediaFiles: GalleryMediaFile[] = [];

    function syncGalleryMediaFiles() {
        galleryMediaFiles = toGalleryMediaFiles(formData.media_files);
    }
</script>

<style>
.hidden {
    display: none;
}

.error {
    color: rgb(228, 27, 27);
    font-size: 0.875em;
}

</style>

{#if $auth.token}
<Modal bind:showModal={displayModal} bind:modalWidth>
    <div slot="header">
        <h2 class="ui header">
            {#if tableData.details.hasMediaFile}
            <Image
                on:delete={handleSingleMediaDelete}
                bind:editMode={isEditing}
                bind:src={singleMediaUrl}
                alt="Media File"
                />
            {/if}
            {tableData.details.titleEdit ? tableData.details.titleEdit : modelName.charAt(0).toUpperCase() + modelName.slice(1)}
        </h2>

        {#if (tableData.details.hasMediaFile) && isEditing}
        <table class="ui compact table unstackable">
            <tbody>
            <tr>
                <td>
                    <input
                        type="file"
                        bind:this={fileInput}
                        class="hidden"
                        bind:files={selectedFiles}
                        multiple={tableData.details.hasMediaFiles}
                        on:change={handleFileSelection}
                        accept={tableData.details.mediaFileType}
                        />
                </td>
                <td>
                    <button on:click={triggerFileInput} class="ui small button">Select</button>
                </td>
            </tr>
            </tbody>
        </table>
        {/if}

    </div>
    <div slot="body">
        <form class="ui form">
            {#each tableData.fields as field (field.key)}
            <div class="field">
                <label>
                    {field.label}:
                    {#if field.type === 'select' && field.select}
                        <select bind:value={formData[field.key]} disabled={isFieldDisabled(field)} on:input={setDirty}>
                            {#each getSelectOptions($api.lists, field.select.data_list) as option (optionProp(option, field.select.key))}
                                <option value={optionProp(option, field.select.key)}>
                                    {optionProp(option, field.select.display)}
                                </option>
                            {/each}
                        </select>
                    {:else if field.type === 'textArea'}
                        <textarea bind:value={formData[field.key]} disabled={isFieldDisabled(field)} on:input={setDirty}></textarea>
                    {:else if field.type === 'date'}
                        <input type="date" bind:value={formData[field.key]} disabled={isFieldDisabled(field)} on:input={setDirty} />
                    {:else if field.type === 'time'}
                        <input type="time" bind:value={formData[field.key]} disabled={isFieldDisabled(field)} on:input={setDirty} />
                    {:else}
                        <input type="text" bind:value={formData[field.key]} disabled={isFieldDisabled(field)} on:input={setDirty} />
                    {/if}
                    {#if errors[field.key]}
                        <span class="error">{errors[field.key]}</span><br>
                    {/if}
                </label>
            </div>
            {/each}
        </form>
        {#if tableData.details.hasMediaFiles}
            <ImageGallery on:delete={handleImageDelete} bind:editMode={isEditing} bind:mediaFiles={galleryMediaFiles}/>
        {/if}
        {#if tableData.details.hasMediaFiles && isEditing}
            <br>
            <input type="file" bind:this={fileInput} style="display: none" bind:files={selectedFiles} multiple={tableData.details.hasMediaFiles} on:change={handleFileSelection} accept={tableData.details.mediaFileType} />
            <button on:click={triggerFileInput} class="button-link" >Select Image Files</button>
        {/if}

    </div>
    <div slot="footer">
        <div class="ui fluid buttons">
            <button on:click|preventDefault={confirmHide} class="ui negative button">Cancel</button>
            {#if tableData.details.edit}
                {#if isEditing}
                    <button on:click|preventDefault={save} class="ui positive button">{ tableData.details.submitLabel ??  'Save' }</button>
                {:else}
                    <button on:click|preventDefault={edit} class="ui blue button">Edit</button>
                {/if}
            {/if}
        </div>
    </div>
</Modal>
<ModalConfirm on:yes="{hide}" bind:showModal={displayConfirm}/>
{:else}
<div class="ui icon message"><i class="info circle icon"></i><div class="content"><p>You must be logged in to view this page.</p></div></div>
{/if}
